import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ScenarioManager from './ScenarioManager';
import CalculationHistory from './CalculationHistory';
import PortfolioSummary from './PortfolioSummary';
import PersonalInfo from './PersonalInfo';
import RiskProfileSurvey from './RiskProfileSurvey';

const { FiUser, FiBarChart3, FiFolder, FiCalendar, FiShield, FiTrendingUp } = FiIcons;

const Portfolio = () => {
  const { user } = useAuth();
  const { scenarios, riskProfile } = useData();
  const [activeTab, setActiveTab] = useState('summary');

  const tabs = [
    { id: 'summary', label: 'Overview', icon: FiBarChart3 },
    { id: 'personal', label: 'Profile', icon: FiUser },
    { id: 'risk', label: 'Risk Profile', icon: FiShield },
    { id: 'scenarios', label: 'Scenarios', icon: FiFolder },
    { id: 'history', label: 'History', icon: FiCalendar }
  ];

  // Helper to format the Target Return as percentage
  const formatTargetReturn = (val) => {
    if (val === null || val === undefined) return "Pending";
    return (val * 100).toFixed(1) + "%";
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-secondary-900 rounded-xl shadow-lg p-6 border border-primary-500/20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center border border-primary-500/30">
                  <SafeIcon icon={FiUser} className="w-8 h-8 text-primary-500" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Welcome, {user.name}</h1>
                  <p className="text-secondary-400">Portfolio Control Panel</p>
                </div>
              </div>

              {riskProfile && (
                <div className="flex items-center space-x-6 bg-black/40 px-8 py-4 rounded-2xl border border-primary-500/10">
                  <div>
                    <p className="text-[10px] font-black text-secondary-500 uppercase tracking-widest mb-1">Risk Profile</p>
                    <p className="text-lg font-bold text-primary-500 uppercase leading-none">{riskProfile.risk_profile}</p>
                  </div>
                  <div className="w-px h-10 bg-secondary-800" />
                  <div>
                    <p className="text-[10px] font-black text-secondary-500 uppercase tracking-widest mb-1">Risk Score</p>
                    <p className="text-lg font-bold text-white leading-none">{riskProfile.client_risk_rating_score}%</p>
                  </div>
                  <div className="w-px h-10 bg-secondary-800" />
                  <div>
                    <p className="text-[10px] font-black text-secondary-500 uppercase tracking-widest mb-1">Target Investment Return</p>
                    <p className="text-lg font-bold text-primary-500 leading-none">
                      {formatTargetReturn(riskProfile["User Target Portfolio Return"])}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-secondary-900 rounded-xl shadow-lg p-1.5 border border-primary-500/20 inline-flex flex-wrap gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-black shadow-lg shadow-primary-500/20'
                    : 'text-secondary-400 hover:text-white hover:bg-secondary-800'
                }`}
              >
                <SafeIcon icon={tab.icon} className="w-4 h-4" />
                <span className="text-sm">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'summary' && <PortfolioSummary />}
            {activeTab === 'personal' && <PersonalInfo />}
            {activeTab === 'risk' && <RiskProfileSurvey />}
            {activeTab === 'scenarios' && <ScenarioManager />}
            {activeTab === 'history' && <CalculationHistory />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Portfolio;